/**
 * ── ⭐ THE LOAD PHASE — "WHAT IS THE BOOT DOING RIGHT NOW", AS A LADDER AND NOT AS A FEELING ─────
 *
 * The owner: *"UE is `LoadMap` → loading screen → `BeginPlay`. We render LIVE FRAMES OF A
 * HALF-BUILT LEVEL with no screen, no progress and no fade-in."* He is right, and the measurable
 * half of that is this file: before it, `mount()` was a straight line of awaits with exactly ONE
 * observable fact in it — `worldResident`, the physics gate (`world/readiness.ts`) — which draws
 * the LAST of six boundaries and says nothing about the five before it.
 *
 * Two windows were opaque, and only the second had ever been named:
 *  · WINDOW A — the blank. Navigation → the first turned frame. The canvas does not exist for most
 *    of it and draws nothing for the rest. NEVER MEASURED before this module; `firstFrameAtMs` is.
 *  · WINDOW B — the frozen live world. First frame → `worldResident` (measured at ~4,205 ms before
 *    the L1.0 yield budget, ~1,500 ms after). The gate correctly stops the SIM and keeps rendering,
 *    so the player watches a real 3D view of a level with no floor and dead input, and nothing on
 *    screen says so.
 *
 * ── WHY A MODULE, AND WHY A LADDER ──────────────────────────────────────────────────────────────
 *
 * Same argument `readiness.ts` makes one level down, and deliberately the same SHAPE, because this
 * module CONSUMES that predicate and must never grow a second one: the phases are defined over
 * BOUND COLLIDERS, never over where the shapes came from. Nothing here may branch on whether a
 * cooked `.col` sidecar existed (L1/L2) — that is what lets the sidecar be measured, kept or rolled
 * back without touching a line of this file or of the surface that draws it.
 *
 * A LADDER, not a state machine: the current phase is the highest rung whose entry fact holds, and
 * a rung is never left. `advance` cannot descend, so a late callback from an earlier stage — a GLB
 * that resolves after the gate opened, a `ready` from a disposed instance — cannot drag the boot
 * backwards. There is exactly ONE re-entry, `restart()`, and it belongs to `mount`'s
 * `spawnEntities()` and to nothing else (the editor's Play transition repopulates the whole host,
 * which really is a second load — see L3.C and `tests/mountSpawn.test.ts`).
 *
 * ⚠️ A RUNG IS ENTERED WHEN THE WORK IT NAMES **BEGINS**, not when that work finishes. That is the
 * only reading under which the two honest counters mean anything: `placed / entities` counts up
 * DURING `placing`, and `bound / required` counts up DURING `streaming`. Written down because the
 * other reading is just as plausible from the rung names, and half a stamp each way is a phase
 * sequence that is monotone, self-consistent and off by one everywhere.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────────────────────────
 *
 * `warming` is NOT a rung. `scene.warmup()` interleaves with asset arrival and reports nothing, and
 * a rung whose boundaries are guesses is a lie in the shape of a state machine. The first TURNED
 * frame is a discrete fact, so it is published as a number (`firstFrameAtMs`) inside `streaming`
 * rather than promoted to a rung of its own.
 *
 * Nor is there a byte-progress or a time-remaining estimate. Refused, each for a checked reason:
 * `WorldFileAssetSchema` carries no size and three's loaders report no `onProgress` here; Sponza's
 * 69 textures are sub-requests that do not exist until the GLB has parsed, so a byte bar would read
 * 0 % → 90 % → stall; and there is no prior for a time estimate (cold and warm differ by an order
 * of magnitude). Per-primitive extraction progress IS countable (`extractCollision` knows i/103)
 * and is the wrong denominator to show a child — it goes to the dev hook and never to a screen.
 *
 * PURE (`PURE_FILES`): no three, no DOM, and NO CLOCK — the wall clock is a banned call in here,
 * scanned. Every entry point takes the time as an argument, exactly as `readiness.ts` takes none,
 * so the whole ladder including its monotonicity is assertable in `npm run check` against a fake
 * clock with no browser.
 */

/**
 * The rungs. `blocked` is OFF-LADDER: it is not a stage of a healthy boot, it is `mount()` having
 * thrown, and it is reachable from any rung.
 */
export type LoadPhase =
  | 'boot'
  | 'world'
  | 'engine'
  | 'placing'
  | 'streaming'
  | 'playing'
  | 'blocked';

/**
 * The ladder, in order. This array IS the ordering — `advance` indexes into it, and the browser
 * tier asserts a real boot's `history` against it verbatim.
 *
 * | rung | what is happening | honestly countable |
 * |---|---|---|
 * | `boot` | the entry chunk has evaluated and `mount()` was entered | nothing. Opaque, and it says so |
 * | `world` | `world.json` is being fetched, parsed and capability-checked (2.8 KB) | nothing worth counting |
 * | `engine` | Rapier's wasm, the scene/sky/lights/camera rig, the host, the exploration session | NOTHING — three awaits with no progress channel |
 * | `placing` | one `EntityInstance` per placement, parented (a `static_mesh` here is an empty group with a fetch in flight) | `placed / entities` |
 * | `streaming` | the GLBs, their textures, the character; extraction; the Rapier bind. The render loop starts INSIDE this rung | `bound / required` — `residency()` verbatim |
 * | `playing` | the gate is open, the sim is ticking | — |
 */
export const LOAD_LADDER: readonly LoadPhase[] = [
  'boot',
  'world',
  'engine',
  'placing',
  'streaming',
  'playing',
];

/** Every phase including the off-ladder one — so a copy map or a switch can be held exhaustive. */
export const ALL_LOAD_PHASES: readonly LoadPhase[] = [...LOAD_LADDER, 'blocked'];

/**
 * Climb. Never descends, and `blocked` is absorbing in both directions: entering it wins from any
 * rung, and nothing climbs back out of it (a boot that threw does not un-throw).
 *
 * A SKIP is allowed and is not an error here — the ladder is "the highest rung whose entry fact
 * holds", and refusing a skip would mean a missed stamp silently freezing the whole model at the
 * rung before it. That every rung is actually stamped is a property of the CALLER, and it is
 * fenced where the caller lives (`tests/mountSpawn.test.ts` reads the stamps out of `mount.ts`).
 */
export function advance(current: LoadPhase, next: LoadPhase): LoadPhase {
  if (current === 'blocked' || next === 'blocked') return 'blocked';
  const a = LOAD_LADDER.indexOf(current);
  const b = LOAD_LADDER.indexOf(next);
  // An unknown rung on either side cannot be ordered; keep what we had rather than invent a jump.
  if (a < 0 || b < 0) return current;
  return LOAD_LADDER[Math.max(a, b)] ?? current;
}

/**
 * ── ⭐ THE BAR RULE — A DETERMINATE BAR IS A CLAIM, AND ON OUR WORLDS IT IS A FALSE ONE ──────────
 *
 * The two honest counters have a fatal property on the very world this whole document is about:
 * `sponza-debug-01` has `entities.length === 2` and `required.length === 1` (counted, not guessed;
 * `playground-01` is 6 and 1). A determinate bar there reads 0 % for the entire load and then
 * 100 % — which is worse than no bar, because it looks like a measurement.
 *
 * So: a determinate bar is drawn ONLY when the counter it would draw has at least this many
 * distinguishable steps. Below that the surface draws the indeterminate mark and the phase word
 * carries the progress. Published to the dev hook as a CONDITIONAL on `required`, never as a
 * constant, so a future world with 40 crates cannot silently relax it.
 */
export const BAR_MIN_STEPS = 4;

/** Would a bar over `required` steps be honest? See `BAR_MIN_STEPS`. */
export function determinate(required: number): boolean {
  return required >= BAR_MIN_STEPS;
}

/** One flat, serialisable read of where the boot is. Everything here is a count or a word. */
export interface LoadProgress {
  phase: LoadPhase;
  /** Placements CONSTRUCTED so far this populate cycle — the `placing` numerator. */
  placed: number;
  /** …and its denominator (`world.entities.length`; 0 before the world has parsed). */
  entities: number;
  /** `residency()`'s own numbers, verbatim — the `streaming` counter. */
  required: number;
  bound: number;
  /** Settled and bound NOTHING (a 404'd asset). Not "missing": it is never coming. */
  broken: number;
  /**
   * ── ⭐ P1 · THE LIBRARY'S ONE COUNTER (ADR-0003 §4.3) ─────────────────────────────────────────
   *
   * Boot-lane library handles: how many distinct asset ids this session asked for at boot, and how
   * many have SETTLED (ready or failed). `playing` waits on `libraryBound === libraryRequired`
   * beside `bound === required` — `world/readiness.worldPlayable` is the one fold of the two.
   *
   * ONE counter, added to the ladder and to nothing else, because that is the whole of what a
   * third distribution track owes the boot: a mid-play summon (`gameplay`) and a store's browse
   * set (`prefetch`) are the SAME machinery in a different lane and gate nothing, so they must not
   * appear here. Both read 0 on a world with no library assets, which is every world today except
   * the ones carrying the character and the companion.
   *
   * ⚠️ NOT PART OF `determinate`. A bar over "1 body + 11 clips" would be an honest 12-step bar of
   * a load whose OTHER half (`required`) is 1 on Sponza — two denominators drawn as one is the
   * dishonesty `BAR_MIN_STEPS` exists to refuse, and choosing between them is a surface decision,
   * not a ladder one. The numbers are published; nothing draws them yet.
   */
  libraryRequired: number;
  libraryBound: number;
}

/** One rung entry, stamped with ms since navigation (the caller's clock — see the header). */
export interface LoadMark {
  phase: LoadPhase;
  atMs: number;
}

/**
 * The ladder, held. One instance lives in `mount()`; the dev hook publishes what it holds.
 *
 * It owns no clock and no console: every method takes `nowMs`, and the caller decides whether a
 * transition is worth a line. That is what makes `history` — the ordered, non-decreasing sequence
 * of rungs a boot actually entered — a headless assertion instead of a screenshot.
 */
export class LoadTracker {
  private cur: LoadPhase = 'boot';
  private readonly marks: LoadMark[] = [];

  constructor(nowMs: number) {
    this.marks.push({ phase: 'boot', atMs: nowMs });
  }

  get phase(): LoadPhase {
    return this.cur;
  }

  /** Every rung this session has entered, in order. THE assertion the browser tier reads. */
  get history(): readonly LoadMark[] {
    return this.marks;
  }

  /**
   * Climb to `next`. Returns the phase after the climb. A no-op climb records nothing, so the
   * history has one row per ENTRY and a caller may stamp defensively.
   */
  to(next: LoadPhase, nowMs: number): LoadPhase {
    return this.set(advance(this.cur, next), nowMs);
  }

  /**
   * ⚠️ THE ONE RE-ENTRY, AND IT HAS EXACTLY ONE CALLER: `mount`'s `spawnEntities()`.
   *
   * F2 → Play (`exitEditor`) does `resetLevel` → `host.dispose()` → `spawnEntities()`, which on
   * Sponza re-walks the whole level in the same place for the same duration. That is a second load
   * and the ladder must say so — before this, `worldResident` was latched forever, so the Play path
   * ran the sim against an EMPTIED entity-collision set: the player sank ~1 m to the ground plane
   * and walked through every wall until the trimeshes rebound. The boot path was protected; the
   * Play path was not.
   *
   * `bindCollision` and the editor's `collision.clear` must NOT come here — that is what the latch
   * was protecting, and it stays protected. The latch now means "since the last spawn" instead of
   * "forever".
   */
  restart(nowMs: number): LoadPhase {
    if (this.cur === 'blocked') return this.cur; // a boot that threw does not restart itself
    return this.set('placing', nowMs);
  }

  /** `mount()` threw. Off-ladder, absorbing, and the only rung with no way forward. */
  block(nowMs: number): LoadPhase {
    return this.set('blocked', nowMs);
  }

  /**
   * When this rung was FIRST entered, in ms since navigation; 0 when it never was. `enteredAt(
   * 'playing')` is Window B's end, and the number a before/after table is judged by.
   */
  enteredAt(phase: LoadPhase): number {
    return this.marks.find((m) => m.phase === phase)?.atMs ?? 0;
  }

  private set(next: LoadPhase, nowMs: number): LoadPhase {
    if (next === this.cur) return this.cur;
    this.cur = next;
    // `atMs` is clamped to the last mark so the sequence is non-decreasing BY CONSTRUCTION rather
    // than by trusting the caller's clock — a monotonicity assertion that can only be broken by
    // deleting this line is worth more than one that depends on the wall clock behaving.
    const last = this.marks[this.marks.length - 1];
    this.marks.push({ phase: next, atMs: Math.max(nowMs, last?.atMs ?? nowMs) });
    return this.cur;
  }
}
